import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import type { WorkflowDisplayTriggerRule } from "@acto/taskpane/core/data-access/workflow";
import type { Conjunction, FilterDescriptor } from "@acto/gql/graphql";
import { LocalizedStringMap } from "@acto/taskpane/core/domain";

interface WorkflowTriggerSectionProps {
  triggerRules: WorkflowDisplayTriggerRule[];
  conjunction: Conjunction;
  availableFilters: FilterDescriptor[];
  onEdit?: () => void;
  isCustomQuery?: boolean;
}

const SectionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[24],
}));

const HeaderRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.managementView.spacing[12],
}));

const HeaderTitleRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flex: 1,
});

const HeaderTitle = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  margin: 0,
}));

const ChangeButton = styled("button")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  backgroundColor: "transparent",
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: "50px",
  cursor: "pointer",
  padding: `${theme.managementView.spacing[18]} ${theme.managementView.spacing[24]}`,
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
}));

const SectionTitle = styled("h3")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[56],
  margin: 0,
}));

const TriggerList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[8],
}));

const TriggerRuleRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
}));

const TriggerRuleBox = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: theme.managementView.radius[10],
  height: "32px",
  flex: 1,
}));

const TriggerField = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: `${theme.managementView.spacing[6]} ${theme.managementView.spacing[6]}`,
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[90],
}));

const TriggerOperator = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: `${theme.managementView.spacing[6]} ${theme.managementView.spacing[12]}`,
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[56],
}));

const TriggerValue = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: `${theme.managementView.spacing[6]} ${theme.managementView.spacing[6]}`,
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[90],
}));

const TriggerConnector = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: `${theme.managementView.spacing[6]}`,
  height: "32px",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[56],
}));

export const WorkflowTriggerSection = ({
  triggerRules,
  conjunction,
  availableFilters,
  onEdit,
  isCustomQuery,
}: WorkflowTriggerSectionProps) => {
  const t = useTranslations("managementView.workflowDetails");
  const tCreate = useTranslations("managementView.workflow.createWorkflow");
  const { locale } = useLocale();

  const filtersMap = new Map(availableFilters.map((f) => [f.name, f]));

  return (
    <SectionContainer>
      <HeaderRow>
        <HeaderTitleRow>
          <HeaderTitle>{t("workflow")}</HeaderTitle>
        </HeaderTitleRow>
        {onEdit && (
          <ChangeButton type="button" onClick={onEdit}>
            {t("change")}
          </ChangeButton>
        )}
      </HeaderRow>
      <SectionTitle>{t("trigger")}</SectionTitle>
      {isCustomQuery ? (
        <TriggerField>{t("customQuery")}</TriggerField>
      ) : (
        <TriggerList>
          {triggerRules.map((rule, index) => {
            const filter = filtersMap.get(rule.field);
            const fieldLabel = filter
              ? LocalizedStringMap.fromGraphql(filter.label).getTranslation(locale)
              : rule.field;
            const operatorLabel = tCreate(`operators.${rule.operator}`, { defaultValue: rule.operator });

            return (
              <TriggerRuleRow key={index}>
                <TriggerRuleBox>
                  <TriggerField>{fieldLabel}</TriggerField>
                  <TriggerOperator>{operatorLabel}</TriggerOperator>
                  <TriggerValue>{rule.value}</TriggerValue>
                  {rule.timeframe && (
                    <TriggerValue>
                      {tCreate(`timeframes.${rule.timeframe}`, { defaultValue: rule.timeframe })}
                    </TriggerValue>
                  )}
                </TriggerRuleBox>
                {index < triggerRules.length - 1 && (
                  <TriggerConnector>
                    {conjunction === "AND" ? tCreate("andOperator") : tCreate("orOperator")}
                  </TriggerConnector>
                )}
              </TriggerRuleRow>
            );
          })}
        </TriggerList>
      )}
    </SectionContainer>
  );
};
